import React, { createContext, useContext, useEffect, useState } from "react";
import { getCurrentUser, getUserById, getUserByEmail, signInWithGoogle, signInWithEmailPassword, signOut, updateUserAuthId, attemptLinking } from "@/lib/firebase";
import { User } from "@/types";
import { useToast } from "@/components/ui/use-toast";

interface AuthContextType {
  currentUser: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (invitationCode?: string) => Promise<void>;
  loginWithEmailPassword: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const refreshUser = async () => {
    try {
      const firebaseUser = await getCurrentUser();
      if (firebaseUser) {
        const user = await getUserById(firebaseUser.uid);
        setCurrentUser(user);
      } else {
        setCurrentUser(null);
      }
    } catch (error) {
      console.error("Error refreshing user:", error);
      toast({
        title: "Error",
        description: "Failed to refresh user data. Please try again.",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      setIsLoading(true);
      try {
        await refreshUser();
      } catch (error) {
        console.error("Authentication error:", error);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (invitationCode?: string) => {
    try {
      setIsLoading(true);
      const result = await signInWithGoogle();
      
      if (result.isNewUser && result.needsLinking && invitationCode) {
        // User is authenticated, wait a bit to ensure Firebase Auth state is established
        console.log("User authenticated, waiting for auth state to establish...");
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Now try to link using the invitation code
        console.log("User authenticated, attempting to link temporary account with code:", invitationCode);
        const linkResult = await attemptLinking(invitationCode);
        
        if (linkResult.success) {
          console.log("Successfully linked temporary user");
          // Refresh user data after successful linking
          await refreshUser();
        } else {
          // Failed to link, show error
          console.log("Failed to link temporary user:", linkResult.error);
          toast({
            title: "Linking Failed",
            description: "Could not link your account. The invitation may be invalid or expired.",
            variant: "destructive",
          });
        }
      } else if (result.isNewUser && !invitationCode) {
        // No invitation code, this is a completely new user
        console.log("No invitation code provided, redirecting to onboarding");
        window.location.href = "/onboarding";
        return; // Don't continue with normal flow
      } else {
        // Existing user (already linked)
        console.log("Existing user, refreshing data");
        await refreshUser();
      }
      
      // For all successful cases except new users without invitation, let the routing handle navigation
      // Don't use window.location.href here to avoid the flash
    } catch (error) {
      console.error("Login error:", error);
      toast({
        title: "Login Failed",
        description: "There was a problem signing in with Google. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithEmailPassword = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      await signInWithEmailPassword(email, password);
      await refreshUser();
    } catch (error) {
      console.error("Email/password login error:", error);
      toast({
        title: "Login Failed",
        description: "There was a problem signing in.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      setIsLoading(true);
      await signOut();
      setCurrentUser(null);
      // Redirect to root instead of /login
      window.location.href = "/";
    } catch (error) {
      console.error("Logout error:", error);
      toast({
        title: "Logout Failed",
        description: "There was a problem signing out. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        isLoading,
        isAuthenticated: !!currentUser,
        login,
        loginWithEmailPassword,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
